import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../../utils/api'

export interface LockHolder {
  id: string
  name: string
}

interface LockResponse {
  data: {
    applies: boolean
    held: boolean
    holder: LockHolder | null
    mine: boolean
    expires_at: string | null
    lease_seconds: number
  }
}

/** `free`: nobody is working on it. `mine`: this person is. `other`: someone else is, so it is read only here. */
export type LockStatus = 'free' | 'mine' | 'other'

export const LOCK_TIMING = {
  /** How often a person who is working renews their lease. Three times inside the lease. */
  heartbeatMs: 20_000,
  /** How often someone who is locked out asks whether it is free. */
  pollMs: 6_000,
  /** Without touching anything for this long, the lock is let go: "active" means working, not having the tab open. */
  idleMs: 120_000,
} as const

/**
 * One person at a time on the company's answer.
 *
 * The page asks for the lock when someone starts working (not when it opens, so
 * reading never blocks anybody), renews it while they do, and lets it go when they
 * submit, stop for a while, or leave. The server is what actually refuses a second
 * writer; this only says so, early, with the name of the person.
 *
 * `enabled` is false where there is nothing to lock: an individual answer, an
 * anonymous one, a survey that is closed or already submitted.
 */
export function useSurveyLock({
  workspaceId,
  surveyId,
  enabled,
  onFreed,
}: {
  workspaceId: string
  surveyId: string
  enabled: boolean
  /** Called once when a lock someone else held has been let go: the answer may have changed meanwhile. */
  onFreed?: () => void
}) {
  const [status, setStatus] = useState<LockStatus>('free')
  const [holder, setHolder] = useState<LockHolder | null>(null)
  const [applies, setApplies] = useState(false)
  const statusRef = useRef<LockStatus>('free')
  const lastActive = useRef(Date.now())
  const freed = useRef(onFreed)
  freed.current = onFreed

  const base = `/api/workspaces/${workspaceId}/surveys/${surveyId}/lock`

  const set = useCallback((next: LockStatus, who: LockHolder | null = null) => {
    statusRef.current = next
    setStatus(next)
    setHolder(next === 'other' ? who : null)
  }, [])

  /** Someone else has it: from a 423 on a save or on asking. */
  const markLocked = useCallback(
    (who: LockHolder | null) => {
      set('other', who)
    },
    [set],
  )

  const acquire = useCallback(async () => {
    if (!enabled) return true
    lastActive.current = Date.now()
    if (statusRef.current === 'mine') return true

    try {
      const response = await api.post<LockResponse>(base)
      if (response.data.applies) set('mine')
      return true
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        set('other', (err.body?.holder as LockHolder | undefined) ?? null)
        return false
      }
      // Anything else (the network, a deploy) must not lock a person out of their own form:
      // the save itself will say if it cannot go through.
      return true
    }
  }, [enabled, base, set])

  const release = useCallback(async () => {
    if (statusRef.current !== 'mine') return
    set('free')
    try {
      await api.delete(base)
    } catch {
      // It runs out by itself within the lease.
    }
  }, [base, set])

  /** Something was done on the page: the person is still working. */
  const touch = useCallback(() => {
    lastActive.current = Date.now()
  }, [])

  // Who has it, when the page opens.
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    api
      .get<LockResponse>(base)
      .then((response) => {
        if (cancelled) return
        setApplies(response.data.applies)
        if (response.data.applies && response.data.held && !response.data.mine) {
          set('other', response.data.holder)
        } else if (response.data.mine) {
          set('mine')
        }
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [enabled, base, set])

  // Not working: ask now and then who is. A person who only has the page open must
  // learn that someone started, as well as that they stopped, or they would find out
  // by being refused.
  useEffect(() => {
    if (!enabled || status === 'mine') return
    const timer = setInterval(async () => {
      try {
        const response = await api.get<LockResponse>(base)
        const someoneElse = response.data.held && !response.data.mine
        if (someoneElse) {
          set('other', response.data.holder)
        } else if (statusRef.current === 'other') {
          set('free')
          freed.current?.()
        }
      } catch {
        // Try again at the next tick.
      }
    }, LOCK_TIMING.pollMs)
    return () => clearInterval(timer)
  }, [enabled, status, base, set])

  // Working: renew while active, let go when idle.
  useEffect(() => {
    if (!enabled || status !== 'mine') return
    const timer = setInterval(() => {
      if (Date.now() - lastActive.current > LOCK_TIMING.idleMs) {
        void release()
        return
      }
      api.post<LockResponse>(base).catch((err) => {
        if (err instanceof ApiError && err.status === 423) {
          set('other', (err.body?.holder as LockHolder | undefined) ?? null)
        }
      })
    }, LOCK_TIMING.heartbeatMs)
    return () => clearInterval(timer)
  }, [enabled, status, base, release, set])

  // Leaving: let go so the next person does not wait for the lease to run out.
  useEffect(() => {
    if (!enabled) return
    const leave = () => {
      if (statusRef.current !== 'mine') return
      const token = localStorage.getItem('token')
      const origin = import.meta.env.VITE_API_URL || ''
      // `keepalive` lets the request outlive the page; a plain one would be cancelled with it.
      void (async () => {
        try {
          await fetch(`${origin}${base}`, {
            method: 'DELETE',
            keepalive: true,
            credentials: 'include',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          })
        } catch {
          // The lease runs out by itself.
        }
      })()
    }
    window.addEventListener('pagehide', leave)
    return () => {
      window.removeEventListener('pagehide', leave)
      leave()
    }
  }, [enabled, base])

  return { status, holder, applies, acquire, release, touch, markLocked }
}
